/**
 * Monogram Personalization — Midcoast Leather
 *
 * Two custom elements:
 *   <monogram-modal>   — wraps the native <dialog>; handles open/close/preview/confirm/variant swap
 *   <monogram-trigger> — the button + confirmation badge on the PDP
 *
 * No framework dependencies. Vanilla ES6.
 */

// Fee variant ID is now set per-product via the custom.monogram_fee_variant_id metafield
// and passed as data-fee-variant-id on <monogram-trigger>. No global constant needed.

// ─────────────────────────────────────────────────────────────
// <monogram-modal>
// ─────────────────────────────────────────────────────────────

class MonogramModal extends HTMLElement {
  /** @type {HTMLDialogElement} */
  #dialog = null;
  /** @type {HTMLInputElement} */
  #input = null;
  /** @type {HTMLSpanElement} */
  #overlayText = null;
  /** @type {HTMLElement} */
  #previewContainer = null;
  /** @type {HTMLImageElement} */
  #previewImage = null;
  /** @type {ResizeObserver} */
  #resizeObserver = null;

  /** px font size at the image's native width (from data attribute) */
  #baseFontSize = 48;

  /** Currently confirmed monogram value — persists across re-opens */
  #confirmedValue = '';

  /** Currently selected finish label, e.g. "Gold Foil" */
  #selectedFinish = 'Gold Foil';
  /** Currently selected finish color hex */
  #selectedFinishColor = '#C9A84C';

  /**
   * Map of variantId (string) → Shopify CDN image URL.
   * Built from data-variant-images JSON attribute.
   * Only contains variants that have their own monogram image.
   * @type {Record<string, string>}
   */
  #variantImages = {};

  /**
   * Map of variantId (string) → { x: number, y: number } position overrides.
   * Falls back to #defaultX / #defaultY when a variant isn't in the map.
   * @type {Record<string, { x: number, y: number }>}
   */
  #variantPositions = {};

  /** Product-level fallback X position (%) */
  #defaultX = 50;
  /** Product-level fallback Y position (%) */
  #defaultY = 45;

  /** CDN URL for the product-level fallback image */
  #productImage = '';

  /** The currently displayed variant ID */
  #currentVariantId = '';

  /** Cleanup function for variant-change listeners */
  #cleanupVariantWatcher = null;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#input = this.querySelector('.monogram-modal__input');
    this.#overlayText = this.querySelector('.monogram-overlay-text');
    this.#previewContainer = this.querySelector('.monogram-preview-container');
    this.#previewImage = this.querySelector('.monogram-preview-image');

    this.#baseFontSize = parseFloat(this.dataset.baseFontSize) || 48;
    this.#productImage = this.dataset.productImage || '';
    this.#currentVariantId = this.dataset.currentVariantId || '';
    this.#defaultX = parseFloat(this.dataset.defaultX) || 50;
    this.#defaultY = parseFloat(this.dataset.defaultY) || 45;

    try {
      this.#variantImages = JSON.parse(this.dataset.variantImages || '{}');
    } catch {
      this.#variantImages = {};
    }

    try {
      this.#variantPositions = JSON.parse(this.dataset.variantPositions || '{}');
    } catch {
      this.#variantPositions = {};
    }

    // Set initial finish state so CSS [data-finish] rules apply immediately on open
    if (this.#overlayText) this.#overlayText.dataset.finish = 'gold';

    // Apply position override for the initially selected variant, if one exists
    if (this.#currentVariantId) this.#updatePosition(this.#currentVariantId);

    this.#bindEvents();
    this.#initResizeObserver();
    this.#watchVariantChanges();
    this.#watchReplaceState();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#cleanupVariantWatcher?.();
    if (this._originalReplaceState) history.replaceState = this._originalReplaceState;
  }

  // ── Public API ──────────────────────────────────────────────

  /**
   * Opens the modal.
   * @param {string|null} variantId — current variant ID read by MonogramTrigger
   *   directly from the product form at click time. This is the authoritative value.
   */
  open(variantId = null) {
    // Sync the preview image to the variant the customer currently has selected.
    // variantId comes straight from the form's hidden [name="id"] input at click time,
    // so it's always correct regardless of whether our background watchers fired.
    const resolvedId = variantId ?? this.#getCurrentVariantId();
    this.#syncImageToVariant(resolvedId);
    // Always update position — #syncImageToVariant returns early when variant hasn't
    // changed, so we need to apply the position override independently here.
    if (resolvedId) this.#updatePosition(resolvedId);

    if (this.#confirmedValue) {
      this.#input.value = this.#confirmedValue;
      this.#updateOverlay(this.#confirmedValue);
    } else {
      this.#input.value = '';
      this.#updateOverlay('');
    }
    this.#updateCharCount();
    this.#dialog.showModal();
    requestAnimationFrame(() => this.#input.focus());
  }

  /**
   * Instantly swaps the preview image for the given variant ID.
   * No cross-fade — called before the dialog is visible so there's nothing to fade.
   * @param {string|null} variantId
   */
  #syncImageToVariant(variantId) {
    if (!variantId || variantId === this.#currentVariantId) return;

    this.#currentVariantId = variantId;
    const newSrc = this.#variantImages[variantId] ?? this.#productImage;
    if (newSrc && this.#previewImage) {
      // Must remove srcset — the Liquid image_tag generates one, and browsers
      // prioritise srcset over src, so setting src alone has no visible effect.
      this.#previewImage.removeAttribute('srcset');
      this.#previewImage.removeAttribute('sizes');
      this.#previewImage.src = newSrc;
      this.#previewImage.addEventListener('load', () => this.#updateScale(), { once: true });
    }
    this.#updatePosition(variantId);
  }

  /**
   * Updates the overlay X/Y position for the given variant.
   * Uses variant-level metafield overrides when set, falls back to product-level defaults.
   * @param {string} variantId
   */
  #updatePosition(variantId) {
    if (!this.#previewContainer) return;
    const pos = this.#variantPositions[variantId];
    const x = pos?.x ?? this.#defaultX;
    const y = pos?.y ?? this.#defaultY;
    this.#previewContainer.style.setProperty('--monogram-x', `${x}%`);
    this.#previewContainer.style.setProperty('--monogram-y', `${y}%`);
  }

  /**
   * Fallback: reads variant ID from the URL ?variant= param.
   * The Horizon theme updates the URL via history.replaceState in variant-picker.js.
   * @returns {string|null}
   */
  #getCurrentVariantId() {
    return new URLSearchParams(window.location.search).get('variant');
  }

  /**
   * Also intercept history.replaceState so we catch URL updates that happen
   * slightly after the click (variant-picker.js uses requestYieldCallback before calling
   * replaceState, so it may fire after our click handler reads the form input).
   * This is a belt-and-suspenders backup to #watchVariantChanges.
   */
  #watchReplaceState() {
    const originalReplaceState = history.replaceState.bind(history);
    history.replaceState = (...args) => {
      originalReplaceState(...args);
      const variantId = new URLSearchParams(window.location.search).get('variant');
      if (variantId) this.#updateImageForVariant(variantId);
    };
    // Store original for cleanup
    this._originalReplaceState = originalReplaceState;
  }

  // ── Private — events ────────────────────────────────────────

  #bindEvents() {
    // Live preview as user types
    this.#input.addEventListener('input', () => {
      const val = this.#input.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
      this.#input.value = val;
      this.#updateOverlay(val);
      this.#updateCharCount();
    });

    // Finish toggle buttons
    this.querySelectorAll('.monogram-finish__option').forEach((btn) => {
      btn.addEventListener('click', () => this.#selectFinish(btn));
    });

    // Confirm
    this.querySelector('.monogram-modal__confirm')?.addEventListener('click', () => this.#confirm());

    // Close buttons (there are two: header X and Cancel button in footer)
    this.querySelectorAll('.monogram-modal__close').forEach((btn) => {
      btn.addEventListener('click', () => this.#close());
    });

    // Click on backdrop
    this.#dialog.addEventListener('click', (e) => {
      if (e.target === this.#dialog) this.#close();
    });

    // Escape key — prevent default close so we can animate
    this.#dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.#close();
    });
  }

  // ── Private — variant change detection ──────────────────────

  /**
   * Listens for the Horizon theme's native 'variant:update' event.
   *
   * Source: variant-picker.js dispatches VariantUpdateEvent (events.js) on the
   * variant-picker element with bubbles:true after the async section fetch resolves.
   * product-form.js catches it on the closest .shopify-section and uses it to
   * update variantId.value — we do the same to swap the preview image.
   *
   * Event detail shape (from events.js JSDoc):
   *   e.detail.resource.id        — the new variant ID (string)
   *   e.detail.data.productId     — the product ID (to scope to this product)
   */
  #watchVariantChanges() {
    const ourProductId = this.dataset.productId;

    const onVariantUpdate = (e) => {
      const variantId = e.detail?.resource?.id;
      const productId = e.detail?.data?.productId;

      // If we know our product ID, ignore events from other products on the page
      if (ourProductId && productId && String(productId) !== String(ourProductId)) return;

      if (variantId) this.#updateImageForVariant(String(variantId));
    };

    document.addEventListener('variant:update', onVariantUpdate);

    this.#cleanupVariantWatcher = () => {
      document.removeEventListener('variant:update', onVariantUpdate);
    };
  }

  /**
   * Swaps the preview image when the variant changes.
   * Uses the variant-specific image if available, falls back to the product-level image.
   * @param {string} variantId
   */
  #updateImageForVariant(variantId) {
    if (variantId === this.#currentVariantId) return;
    this.#currentVariantId = variantId;

    const newSrc = this.#variantImages[variantId] ?? this.#productImage;
    if (!newSrc || !this.#previewImage) return;

    // Cross-fade: fade out → swap src → fade in
    this.#previewImage.style.transition = 'opacity 0.2s ease';
    this.#previewImage.style.opacity = '0';

    const swapSrc = () => {
      // Remove srcset/sizes — browsers prioritise srcset over src, so setting
      // src alone while srcset still references the old image has no effect.
      this.#previewImage.removeAttribute('srcset');
      this.#previewImage.removeAttribute('sizes');
      this.#previewImage.src = newSrc;
      this.#previewImage.style.opacity = '1';
      // Reset ResizeObserver scale after image loads
      this.#previewImage.addEventListener(
        'load',
        () => this.#updateScale(),
        { once: true }
      );
    };

    // Use a small timeout to let the fade-out render
    setTimeout(swapSrc, 200);

    this.#updatePosition(variantId);
  }

  // ── Private — overlay & scale ────────────────────────────────

  #selectFinish(btn) {
    // Update selected state on all buttons
    this.querySelectorAll('.monogram-finish__option').forEach((b) => {
      b.classList.remove('monogram-finish__option--selected');
      b.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('monogram-finish__option--selected');
    btn.setAttribute('aria-pressed', 'true');

    // Store selection
    this.#selectedFinish = btn.dataset.finish;
    this.#selectedFinishColor = btn.dataset.color;

    // Drive the CSS finish styles via data-finish attribute on the overlay element.
    // Gold gets the animated shimmer gradient; burned gets a solid dark color.
    // (background-clip: text requires this approach — can't use a CSS custom property.)
    if (this.#overlayText) {
      this.#overlayText.dataset.finish = btn.dataset.finish === 'Gold Foil' ? 'gold' : 'burned';
    }
  }

  #confirm() {
    const val = this.#input.value.trim().toUpperCase();
    this.#confirmedValue = val;
    this.dispatchEvent(
      new CustomEvent('monogram:confirmed', {
        bubbles: true,
        detail: { value: val, finish: this.#selectedFinish },
      })
    );
    this.#close();
  }

  #close() {
    this.#dialog.classList.add('monogram-modal--closing');
    const onEnd = () => {
      this.#dialog.classList.remove('monogram-modal--closing');
      this.#dialog.close();
      this.#dialog.removeEventListener('animationend', onEnd);
    };
    this.#dialog.addEventListener('animationend', onEnd);
  }

  #updateOverlay(text) {
    if (this.#overlayText) {
      this.#overlayText.textContent = text || '\u00A0';
    }
  }

  #updateCharCount() {
    const counter = this.querySelector('.monogram-modal__char-count');
    if (counter) counter.textContent = `${this.#input.value.length}/3`;
  }

  /**
   * Scales the overlay font proportionally to the modal container width.
   * Uses a fixed reference width (560px = the modal's CSS max-width) so that
   * monogram_font_size always means "px at full modal width" regardless of
   * the image's natural pixel dimensions.
   */
  #updateScale() {
    if (!this.#previewContainer) return;
    const containerWidth = this.#previewContainer.offsetWidth;
    if (!containerWidth) return;
    const scale = containerWidth / 560;
    this.#previewContainer.style.setProperty('--monogram-scale', scale.toFixed(4));
  }

  #initResizeObserver() {
    if (!this.#previewContainer) return;

    this.#resizeObserver = new ResizeObserver(() => this.#updateScale());
    this.#resizeObserver.observe(this.#previewContainer);
    this.#updateScale();
  }
}

customElements.define('monogram-modal', MonogramModal);


// ─────────────────────────────────────────────────────────────
// <monogram-trigger>
// ─────────────────────────────────────────────────────────────

class MonogramTrigger extends HTMLElement {
  /** @type {HTMLInputElement} — hidden form input for properties[Monogram] */
  #formInput = null;
  /** @type {HTMLInputElement} — hidden form input for properties[Monogram Finish] */
  #finishInput = null;
  /** @type {MonogramModal} */
  #modal = null;
  /** @type {string} — variant ID for the monogram service fee, set via metafield per product */
  #feeVariantId = '';

  connectedCallback() {
    const inputId = this.dataset.inputId;
    if (inputId) this.#formInput = document.getElementById(inputId);

    const finishInputId = this.dataset.finishInputId;
    if (finishInputId) this.#finishInput = document.getElementById(finishInputId);

    const modalId = this.dataset.modalId;
    if (modalId) this.#modal = document.getElementById(modalId);

    this.#feeVariantId = this.dataset.feeVariantId || '';

    this.#bindEvents();
  }

  #bindEvents() {
    this.querySelector('.monogram-trigger__button')?.addEventListener('click', () => {
      const form = this.#formInput?.closest('form');
      const currentVariantId = form?.querySelector('[name="id"]')?.value ?? null;
      this.#modal?.open(currentVariantId);
    });

    // The modal dispatches this event with bubbles:true, so it reaches the document
    document.addEventListener('monogram:confirmed', (e) => {
      this.#handleConfirmed(e.detail.value, e.detail.finish);
    });
  }

  #handleConfirmed(value, finish) {
    if (this.#formInput) this.#formInput.value = value;
    if (this.#finishInput) this.#finishInput.value = finish ?? '';

    const badge = this.querySelector('.monogram-confirmation');
    const badgeText = this.querySelector('.monogram-confirmation__text');
    if (badge && badgeText) {
      badgeText.textContent = value ? `${value} · ${finish}` : '';
      badge.hidden = !value;
    }

    if (value) {
      const form = this.#formInput?.closest('form');
      const variantId = form?.querySelector('[name="id"]')?.value ?? '';
      const submitBtn = form?.querySelector('[type="submit"]');

      if (this.#feeVariantId) {
        // Add fee first so both items are in the cart when the drawer opens
        this.#addFeeToCart(variantId).then(() => submitBtn?.click());
      } else {
        submitBtn?.click();
      }
    }
  }

  /**
   * Adds the monogram service fee as a separate line item.
   * Fee variant ID comes from the product's custom.monogram_fee_variant_id metafield.
   * Stores _monogram_for so the cart watcher can detect the association.
   * @param {string} parentVariantId — the variant the customer is buying
   */
  async #addFeeToCart(parentVariantId) {
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: this.#feeVariantId,
            quantity: 1,
            properties: { '_monogram_for': parentVariantId },
          }],
        }),
      });
    } catch {
      // Fee add failed silently — not a blocker for the monogram itself
    }
  }
}

customElements.define('monogram-trigger', MonogramTrigger);
